/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Check, Clock, MapPin, Briefcase, Heart, Eye, Sparkles, Calendar } from 'lucide-react';
import { AvatarDisplay } from '../../utils/avatars';
import { getZodiacSign, calculateAge, isBirthdayToday } from '../../utils/zodiac';
import { getMbtiStyle } from '../../utils/mbti';

// MBTI 標籤：依四大群組配色，未填寫不渲染
const MbtiTag = ({ mbti, style: extraStyle }) => {
  const mbtiStyle = getMbtiStyle(mbti);
  if (!mbtiStyle) return null;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '8px',
      background: mbtiStyle.bg,
      color: mbtiStyle.color,
      fontSize: '11px',
      fontWeight: '700',
      letterSpacing: '0.05em',
      flexShrink: 0,
      verticalAlign: 'middle',
      ...extraStyle
    }}>
      {mbti}
    </span>
  );
};

// Re-designed UserCard component with inline styles and no external dependencies
const UserCard = ({ user, onAddFriend, friendshipStatus, friendshipData, currentUserId, viewMode = 'grid', onViewProfile, onAcceptFriend, onDeclineFriend, onCancelRequest, sharedActivitiesCount = 0, currentUserInterests = [] }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleViewProfile = () => {
    if (onViewProfile) {
      onViewProfile(user);
    }
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    border: '1px solid #f0f0f0',
  };

  const avatarStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #eef4f9', // A light green border
  };

  const nameStyle = {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#333',
  };

  const infoStyle = {
    fontSize: '0.9rem',
    color: '#666',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  };

  const bioStyle = {
    fontSize: '0.95rem',
    color: '#555',
    lineHeight: '1.5',
  };

  const buttonStyle = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  };

  const getButtonState = () => {
    if (friendshipStatus === 'pending' && friendshipData) {
      // Check if current user received the request (can accept/decline)
      const receivedRequest = friendshipData.friend._id === currentUserId;
      if (receivedRequest) {
        return {
          type: 'pending_received',
          text: '接受好友',
          disabled: false,
          style: { ...buttonStyle, background: '#059669', color: 'white' },
          icon: <Check size={16} />
        };
      } else {
        // Current user sent the request
        return {
          type: 'pending_sent',
          text: '收回邀請',
          disabled: false,
          style: { ...buttonStyle, background: '#f59e0b', color: 'white' },
          icon: <Clock size={16} />
        };
      }
    }

    switch (friendshipStatus) {
      case 'accepted':
        return {
          type: 'accepted',
          text: '已是好友',
          disabled: true,
          style: { ...buttonStyle, background: '#eef4f9', color: '#000000ff' },
          icon: <Check size={16} />
        };
      default:
        return {
          type: 'none',
          text: '加為好友',
          disabled: false,
          style: { ...buttonStyle, background: '#376ba1', color: 'white' },
          icon: <User size={16} />
        };
    }
  };

  const { text, disabled, style, icon, type } = getButtonState();

  const handleButtonClick = () => {
    if (type === 'pending_received' && onAcceptFriend) {
      onAcceptFriend(friendshipData._id);
    } else if (type === 'pending_sent' && onCancelRequest) {
      onCancelRequest(friendshipData._id);
    } else if (type === 'none' && onAddFriend) {
      onAddFriend();
    }
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        style={{
          background: 'white',
          borderRadius: isMobile ? '12px' : '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          padding: '16px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: '16px',
          border: '1px solid #f1f5f9',
          transition: 'all 0.3s ease',
          height: 'auto',
          minHeight: isMobile ? 'auto' : '120px',
          cursor: 'pointer'
        }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        onClick={handleViewProfile}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
        }}
      >
        {/* Avatar and Name - Mobile: Horizontal, Desktop: Avatar only */}
        {isMobile ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '100%'
          }}>
            <div style={{ flexShrink: 0 }}>
              <AvatarDisplay avatarId={user.avatar || 'avatar1'} size={60} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  color: '#2d3748',
                  margin: 0,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', 'Microsoft JhengHei', sans-serif",
                  letterSpacing: '-0.02em'
                }}>
                  {user.name || '未提供姓名'}
                </h3>
                <MbtiTag mbti={user.mbti} />
                {user.badge === 'founding' && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    padding: '2px 7px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #eef4f9, #fde68a)',
                    border: '1px solid #f59e0b',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#92400e',
                    flexShrink: 0
                  }}>
                    🏅 創始會員
                  </span>
                )}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: '#6b7280',
                flexWrap: 'wrap',
                lineHeight: '1.6',
                marginTop: '4px'
              }}>
                {user.gender && (
                  <>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <User size={12} /> {user.gender}
                    </span>
                    <span style={{ color: '#d1d5db' }}>•</span>
                  </>
                )}
                <span>{(() => { const a = user.birthday ? calculateAge(user.birthday) : user.age; return `${a || 'N/A'} 歲`; })()}</span>
                {user.birthday && getZodiacSign(user.birthday) && (
                  <>
                    <span style={{ color: '#d1d5db' }}>•</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Sparkles size={12} color="#8b5cf6" />
                      {getZodiacSign(user.birthday).name}
                    </span>
                  </>
                )}
                {user.city && (
                  <>
                    <span style={{ color: '#d1d5db' }}>•</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={12} /> {user.city}
                    </span>
                  </>
                )}
                {user.occupation && (
                  <>
                    <span style={{ color: '#d1d5db' }}>•</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Briefcase size={12} /> {user.occupation}
                    </span>
                  </>
                )}
                {user.birthday && isBirthdayToday(user.birthday) && <span>🎂</span>}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ flexShrink: 0 }}>
            <AvatarDisplay avatarId={user.avatar || 'avatar1'} size={70} />
          </div>
        )}

        {/* User Info - Desktop only shows full info */}
        <div style={{
          flex: 1,
          minWidth: 0,
          width: isMobile ? '100%' : 'auto'
        }}>
          {!isMobile && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  color: '#2d3748',
                  margin: 0,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', 'Microsoft JhengHei', sans-serif",
                  letterSpacing: '-0.02em'
                }}>
                  {user.name || '未提供姓名'}
                </h3>
                <MbtiTag mbti={user.mbti} />
                {user.badge === 'founding' && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #eef4f9, #fde68a)',
                    border: '1px solid #f59e0b',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#92400e'
                  }}>
                    🏅 創始會員
                  </span>
                )}
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: '#6b7280',
                marginBottom: '10px',
                flexWrap: 'wrap',
                lineHeight: '1.6'
              }}>
                {user.gender && (
                  <>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <User size={13} /> {user.gender}
                    </span>
                    <span style={{ color: '#d1d5db' }}>•</span>
                  </>
                )}
                <span>{(() => { const a = user.birthday ? calculateAge(user.birthday) : user.age; return `${a || 'N/A'} 歲`; })()}</span>
                {user.birthday && getZodiacSign(user.birthday) && (
                  <>
                    <span style={{ color: '#d1d5db' }}>•</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Sparkles size={13} color="#8b5cf6" />
                      {getZodiacSign(user.birthday).name}
                    </span>
                  </>
                )}
                {user.city && (
                  <>
                    <span style={{ color: '#d1d5db' }}>•</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={13} /> {user.city}
                    </span>
                  </>
                )}
                {user.occupation && (
                  <>
                    <span style={{ color: '#d1d5db' }}>•</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Briefcase size={13} /> {user.occupation}
                    </span>
                  </>
                )}
                {user.birthday && isBirthdayToday(user.birthday) && <span>🎂</span>}
              </div>
            </>
          )}

          {/* Mobile: Detailed info is already in the header part above for layout consistency */}

          {/* 共同活動標籤 */}
          {sharedActivitiesCount > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                <Calendar size={12} />
                已共同參加 {sharedActivitiesCount} 次活動
              </span>
            </div>
          )}

          {/* 興趣標籤 */}
          {user.interests && user.interests.length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '8px'
            }}>
              {[...user.interests]
                .sort((a, b) =>
                  (currentUserInterests.includes(b) ? 1 : 0) - (currentUserInterests.includes(a) ? 1 : 0)
                )
                .map((interest, index) => {
                // 與登入者共同的興趣用實心藍標示並排在最前面
                const isShared = currentUserInterests.includes(interest);
                return (
                  <span
                    key={index}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '14px',
                      background: isShared ? '#4e8bca' : '#fff',
                      color: isShared ? '#fff' : '#4e8bca',
                      border: isShared ? '1px solid #4e8bca' : '1px solid #cfe0ef',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}
                  >
                    {interest}
                  </span>
                );
              })}
            </div>
          )}

          {/* 自我介紹（一兩行） */}
          {user.bio && user.bio !== '未填寫' && (
            <p style={{
              fontSize: '13px',
              color: '#4b5563',
              lineHeight: '1.5',
              margin: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              marginTop: '4px'
            }}>
              {user.bio}
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  // Grid mode (card view)
  return (
    <motion.div
      style={{
        ...cardStyle,
        borderRadius: '24px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
        padding: '24px',
        transition: 'all 0.3s ease'
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onClick={handleViewProfile}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.1)';
      }}
    >
      {/* Avatar */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
        <AvatarDisplay avatarId={user.avatar || 'avatar1'} size={80} />
      </div>

      {/* Name */}
      <h3 style={{
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#2d3748',
        textAlign: 'center',
        marginBottom: '4px',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', 'Microsoft JhengHei', sans-serif",
        letterSpacing: '-0.02em'
      }}>
        {user.name || '未提供姓名'}
        <MbtiTag mbti={user.mbti} style={{ marginLeft: '8px', fontSize: '12px' }} />
      </h3>
      {user.badge === 'founding' && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '8px'
        }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #eef4f9, #fde68a)',
            border: '1px solid #f59e0b',
            fontSize: '12px',
            fontWeight: '600',
            color: '#92400e'
          }}>
            🏅 創始會員
          </span>
        </div>
      )}

      {/* Basic Info with Icons */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '13px',
        color: '#6b7280',
        marginBottom: '16px',
        flexWrap: 'wrap',
        lineHeight: '1.6'
      }}>
        {user.gender && (
          <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <User size={13} /> {user.gender}
            </span>
            <span style={{ color: '#d1d5db' }}>•</span>
          </>
        )}
        <span>{(() => { const a = user.birthday ? calculateAge(user.birthday) : user.age; return `${a || 'N/A'} 歲`; })()}</span>
        {user.birthday && getZodiacSign(user.birthday) && (
          <>
            <span style={{ color: '#d1d5db' }}>•</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Sparkles size={13} color="#8b5cf6" />
              {getZodiacSign(user.birthday).name}
            </span>
          </>
        )}
        {user.city && (
          <>
            <span style={{ color: '#d1d5db' }}>•</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <MapPin size={13} /> {user.city}
            </span>
          </>
        )}
        {user.occupation && (
          <>
            <span style={{ color: '#d1d5db' }}>•</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Briefcase size={13} /> {user.occupation}
            </span>
          </>
        )}
        {user.birthday && isBirthdayToday(user.birthday) && <span>🎂</span>}
      </div>

      {/* 共同活動標籤 (Grid) */}
      {sharedActivitiesCount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #4e8bca 0%, #6a9fd3 100%)',
            color: '#fff',
            fontSize: '11px',
            fontWeight: '600'
          }}>
            <Calendar size={12} />
            已共同參加 {sharedActivitiesCount} 次活動
          </span>
        </div>
      )}

    </motion.div>
  );
};

export default UserCard;
